/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useRef, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import OtpInput from '../../../common/otp-input/otp-input.component';

import { TextBody } from '../../../common/typography/typography.styles';
import Button from '../../../common/button/button.component';
import { useStore } from '../../../stores/store';
import { AppRoute } from '../../../constants/route';
import { generateLocaleRoute } from '../../../utils/uri/uri.utils';
import { theme } from '../../../styles/themes';
import { isMeest } from '../../../utils/client/client.utils';
import { client } from '../../../config';
import {
  AddPhoneHeader,
  AuthMessageScreen,
  AuthMessageScreenCenteredElement,
  Buttons,
  PhoneNumberForm,
} from './confirm-phone-number.styles';
import ModalDialog from '../../../common/modal-dialog/modal-dialog.component';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import FormFieldPhone, {
  getCountryCallingCodeWrapper,
} from '../../../common/form-field-phone/form-field-phone.component';
import { ContactDetailsValues } from '../../../models/contactDetailsValues';
import {
  contactDetailsValidationSchema,
  removeCountryCallingCodeFromPhone,
} from '../contact-details-form.component';

export const CONFIRM_NUMBER_DIGITS_AMOUNT = 4;

interface Props {
  isOpen: boolean;
  onSuccessfulUpdate?: () => void;
  phoneNumberInputRequired?: boolean;
}

type PhoneNumberValues = Pick<
  ContactDetailsValues,
  'phone' | 'phone_country_code'
>;

const ConfirmPhoneNumberModal = ({
  isOpen,
  onSuccessfulUpdate,
  phoneNumberInputRequired = false,
}: Props) => {
  const {
    commonStore: { toastSuccess },
    userStore: {
      user,
      isLoadingUser,
      isNeedMigrationAgreement,
      isPhoneVerifying,
      updatePhone,
      setPhoneForVerification,
      verifyPhone,
    },
    parcelCreationStore: { sendVerificationMessage },
    localizationsStore: { availableCountries, selectedCountry },
  } = useStore();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeModal } = useModal();

  const [verifyCode, setVerifyCode] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isPhoneNumberInputVisible, setIsPhoneNumberInputVisible] = useState(
    phoneNumberInputRequired
  );
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const isFilled = verifyCode.length === CONFIRM_NUMBER_DIGITS_AMOUNT;

  useEffect(() => {
    if (isFilled) {
      confirmButtonRef.current?.focus();
    }
  }, [isFilled]);

  if (!isPhoneNumberInputVisible && !user?.phoneForVerification) return null;

  const { phone_number: phoneNumber, country_code: phoneCountry } =
    user?.phoneForVerification ?? { phone_number: '', country_code: '' };

  const initialPhoneValues: PhoneNumberValues = {
    phone:
      removeCountryCallingCodeFromPhone(
        user?.phone?.phone_number,
        user?.phone?.country_code
      ) ?? '',
    phone_country_code:
      user?.phone?.country_code ?? selectedCountry?.country_code ?? 'US',
  };

  const handlePhoneNumberSubmit = async (
    formValues: PhoneNumberValues,
    formikHelpers: FormikHelpers<PhoneNumberValues>
  ) => {
    const newPhone = {
      phone_number: `${getCountryCallingCodeWrapper(formValues.phone_country_code)}${formValues.phone}`,
      country_code: formValues.phone_country_code,
    };

    const { requiresVerification, error } = await updatePhone(newPhone);

    if (error) {
      formikHelpers.setFieldError('phone', error);
      return;
    }

    if (requiresVerification) {
      setPhoneForVerification(newPhone);
      setIsPhoneNumberInputVisible(false);
      return;
    }

    toastSuccess(t('data_updated_successfully'));
    onSuccessfulUpdate?.();
    closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION);
  };

  const handleSubmit = async () => {
    const success = await verifyPhone(phoneNumber, phoneCountry, verifyCode);

    if (success) {
      onSuccessfulUpdate?.();
      toastSuccess(t('successfully_verified_phone'));
      closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION);

      if (isNeedMigrationAgreement) {
        navigate(generateLocaleRoute(`${AppRoute.AUTH}`), {
          replace: true,
        });
      }
    } else {
      setIsCodeValid(false);
    }
  };

  async function handleSendAgain() {
    if (!user?.phoneForVerification) return;

    await sendVerificationMessage(user.phoneForVerification);
  }

  if (isPhoneNumberInputVisible) {
    return (
      <ModalDialog
        isOpen={isOpen}
        displayButtons={false}
        onClose={() => closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION)}
        width='52.8rem'
        height='60rem'
        isMobileFullScreen
        mobileTopOffset='5.6rem'
        padding='4rem'>
        <AuthMessageScreen>
          <AuthMessageScreenCenteredElement>
            <AddPhoneHeader>{t('add_your_phone_number')}</AddPhoneHeader>
          </AuthMessageScreenCenteredElement>
          <Formik
            initialValues={initialPhoneValues}
            validationSchema={contactDetailsValidationSchema.pick([
              'phone',
              'phone_country_code',
            ])}
            validateOnMount
            onSubmit={handlePhoneNumberSubmit}>
            {({ submitForm, isValid }) => (
              <PhoneNumberForm
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  submitForm();
                }}>
                <FormFieldPhone
                  $mb='0'
                  phoneFieldName='phone'
                  codeFieldName='phone_country_code'
                  placeholder='(123) 456 - 7890'
                  label={t('phone_number')}
                  allowedPhoneCountries={
                    availableCountries?.map((x) => x.countryDetails) ?? []
                  }
                  defaultCountryCode={selectedCountry?.country_code}
                />
                <Buttons $gap='1.6rem'>
                  <Button
                    size='large'
                    type='submit'
                    disabled={!isValid}
                    isLoading={isLoadingUser}>
                    {t('next')}
                  </Button>
                  <Button
                    appearance='text'
                    size='large'
                    disabled={isLoadingUser}
                    onClick={() =>
                      closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION)
                    }>
                    {t('cancel')}
                  </Button>
                </Buttons>
              </PhoneNumberForm>
            )}
          </Formik>
        </AuthMessageScreen>
      </ModalDialog>
    );
  }

  return (
    <ModalDialog
      approveBtnContent={t('verify')}
      cancelBtnContent={t('back')}
      isOpen={isOpen}
      displayButtons={false}
      onApprove={() => {}}
      onClose={() => closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION)}
      width='52.8rem'
      height='60rem'
      isMobileFullScreen
      mobileTopOffset='5.6rem'
      padding='4rem'>
      <AuthMessageScreen>
        <AuthMessageScreenCenteredElement>
          <AddPhoneHeader>{t('confirm_the_phone_number')}</AddPhoneHeader>
        </AuthMessageScreenCenteredElement>
        <TextBody $color={theme.colors.baseColors.grey100}>
          <Trans
            i18nKey='phone_sent_signup'
            components={{
              1: <b />,
            }}
            values={{ phoneNumber: `+${phoneNumber}` }}
          />
        </TextBody>

        <OtpInput
          handleSendAgain={handleSendAgain}
          numOfDigits={CONFIRM_NUMBER_DIGITS_AMOUNT}
          onChange={(val: string) => {
            setVerifyCode(val);
            setIsCodeValid(true);
          }}
          isInvalid={!isCodeValid}
          errorMessage={t('incorrect_verification_code')}
          fullWidth
          titleColor={theme.colors.baseColors.grey100}
          title={t('enter_code_from_sms')}
        />
        <Buttons $gap='1.6rem'>
          <Button
            size='large'
            onClick={handleSubmit}
            disabled={!isFilled || !isCodeValid || isPhoneVerifying}
            isLoading={isLoadingUser || isPhoneVerifying}
            ref={confirmButtonRef}>
            {t('confirm')}
          </Button>
          <Button
            appearance={isMeest(client) ? 'text' : 'secondary'}
            disabled={isLoadingUser}
            size='large'
            onClick={() => {
              closeModal(Modals.USER_SETTINGS_PHONE_VERIFICATION);
            }}>
            {t('back')}
          </Button>
        </Buttons>
      </AuthMessageScreen>
    </ModalDialog>
  );
};

export default observer(ConfirmPhoneNumberModal);
